// eslint-disable-next-line import/no-extraneous-dependencies
import { AwsStageType } from "@codedrifters/utils";
import { Component } from "projen";
import { AwsCdkTypeScriptApp } from "projen/lib/awscdk";
import { AwsDeploymentTarget } from "./aws-deployment-target";
import {
  CDK_BOOTSTRAP_DEFAULTS_BY_STAGE,
  CDK_DEPLOY_DEFAULTS_BY_STAGE,
  CDK_DESTROY_DEFAULTS_BY_STAGE,
  CDK_DIFF_DEFAULTS_BY_STAGE,
  CDK_SYNTH_DEFAULTS_BY_STAGE,
  CDK_WATCH_DEFAULTS_BY_STAGE,
} from "./cdk-cli-defaults";
import { mergeCdkOptions } from "./cdk-cli-merge";
import {
  CdkBootstrapOptions,
  CdkDeployOptions,
  CdkDestroyOptions,
  CdkDiffOptions,
  CdkSynthOptions,
  CdkTargetOverrides,
  CdkWatchOptions,
} from "./cdk-cli-options";

/*******************************************************************************
 *
 * AWS CDK CLI Component
 *
 * Owns the per-project options that get layered into every `cdk` CLI
 * invocation the generated project makes. Phase 1 covered `cdk deploy`; Phase
 * 2 extended the component with `synth`, `watch`, `destroy`, `diff`, and
 * `bootstrap`. Phase 5 (this revision) wires in the full per-target /
 * per-account override surface and the generic {@link mergeCdkOptions} folder.
 *
 * ## Override precedence
 *
 * Each `*OptionsFor(target)` accessor folds the layers below in
 * **lowest-precedence-first** order; the rightmost (highest) layer wins on
 * scalar collisions, arrays concat-and-dedupe across layers, and record-valued
 * fields merge one level deep with the higher layer winning on key collisions.
 *
 * 1. **Per-stage built-in default** — from `CDK_*_DEFAULTS_BY_STAGE` for the
 *    target's {@link AwsDeploymentTarget.awsStageType}.
 * 2. **Per-stage consumer override** — from this component's
 *    `<command>Defaults[stage]` map.
 * 3. **Per-account override** — from `accountOverrides[target.account][<command>]`.
 * 4. **Per-target override** — from `target.cdkOptions.<command>`.
 * 5. **Call-site explicit option** — passed by the caller into the renderer
 *    directly, after the accessor returns. The accessor does not see this
 *    layer; the caller is responsible for spreading the accessor result first
 *    and then the call-site option on top.
 *
 * ## CLI version
 *
 * Beyond per-command flags, the component also owns {@link CdkCli.cliVersion} —
 * the `aws-cdk` version generated CI jobs install before invoking `cdk`. It
 * defaults to the project's own `aws-cdk` requirement so a deploy runs the same
 * CLI that synthesized the cloud assembly it is deploying.
 *
 * Auto-instantiated by {@link AwsDeploymentConfig} when absent; consumers can
 * pre-instantiate it via `new CdkCli(project, options)` to register custom
 * per-stage defaults, per-account overrides, and a pinned CLI version.
 *
 ******************************************************************************/

/**
 * Options accepted by {@link CdkCli}.
 */
export interface CdkCliOptions {
  /**
   * Consumer-supplied overrides for the built-in per-stage deploy defaults.
   *
   * Each entry is merged on top of
   * {@link CDK_DEPLOY_DEFAULTS_BY_STAGE} for the matching stage via
   * {@link mergeCdkOptions}.
   */
  readonly deployDefaults?: Partial<
    Record<AwsStageType, Partial<CdkDeployOptions>>
  >;

  /**
   * Consumer-supplied overrides for the built-in per-stage synth defaults.
   *
   * Each entry is merged on top of
   * {@link CDK_SYNTH_DEFAULTS_BY_STAGE} for the matching stage via
   * {@link mergeCdkOptions}.
   */
  readonly synthDefaults?: Partial<
    Record<AwsStageType, Partial<CdkSynthOptions>>
  >;

  /**
   * Consumer-supplied overrides for the built-in per-stage watch defaults.
   *
   * Each entry is merged on top of
   * {@link CDK_WATCH_DEFAULTS_BY_STAGE} for the matching stage via
   * {@link mergeCdkOptions}.
   */
  readonly watchDefaults?: Partial<
    Record<AwsStageType, Partial<CdkWatchOptions>>
  >;

  /**
   * Consumer-supplied overrides for the built-in per-stage destroy defaults.
   *
   * Each entry is merged on top of
   * {@link CDK_DESTROY_DEFAULTS_BY_STAGE} for the matching stage via
   * {@link mergeCdkOptions}.
   */
  readonly destroyDefaults?: Partial<
    Record<AwsStageType, Partial<CdkDestroyOptions>>
  >;

  /**
   * Consumer-supplied overrides for the built-in per-stage diff defaults.
   *
   * Each entry is merged on top of
   * {@link CDK_DIFF_DEFAULTS_BY_STAGE} for the matching stage via
   * {@link mergeCdkOptions}.
   */
  readonly diffDefaults?: Partial<
    Record<AwsStageType, Partial<CdkDiffOptions>>
  >;

  /**
   * Consumer-supplied overrides for the built-in per-stage bootstrap defaults.
   *
   * Each entry is merged on top of
   * {@link CDK_BOOTSTRAP_DEFAULTS_BY_STAGE} for the matching stage via
   * {@link mergeCdkOptions}.
   */
  readonly bootstrapDefaults?: Partial<
    Record<AwsStageType, Partial<CdkBootstrapOptions>>
  >;

  /**
   * Per-account overrides keyed by AWS account ID.
   *
   * Each value is a {@link CdkTargetOverrides} bundle that applies to every
   * {@link AwsDeploymentTarget} whose `account` matches the key. Sits in the
   * precedence chain between per-stage overrides and per-target overrides —
   * see {@link CdkCli} for the full layer order.
   *
   * Example: pin `--no-rollback` on every deploy into a sandbox account
   * regardless of the target's stage:
   *
   * ```typescript
   * new CdkCli(project, {
   *   accountOverrides: {
   *     "111122223333": { deploy: { rollback: false } },
   *   },
   * });
   * ```
   */
  readonly accountOverrides?: Record<string, CdkTargetOverrides>;

  /**
   * The `aws-cdk` CLI version CI jobs install before invoking `cdk`.
   *
   * Rendered into the generated deploy and diff jobs as
   * `pnpm dlx "aws-cdk@<version>"`, so a CI run deploys a cloud assembly with
   * the same CLI that synthesized it instead of whatever happens to be latest
   * on the registry at run time.
   *
   * Accepts any npm version specifier. Supplying a range (`^2`) rather than an
   * exact version keeps the drift this option exists to close, so prefer an
   * exact pin.
   *
   * @default - the project's own `aws-cdk` dependency requirement
   * (`cdkDeps.cdkCliVersion`), which is the CLI that runs the `synth` task and
   * therefore produces the cloud assembly CI deploys. Projects created through
   * configulator's `AwsCdkProject` pin this exactly; a bare projen
   * `AwsCdkTypeScriptApp` that never set `cdkCliVersion` falls back to projen's
   * own `^2` default.
   */
  readonly cliVersion?: string;
}

export class CdkCli extends Component {
  /**
   * Find the {@link CdkCli} component already attached to the project, if any.
   */
  public static of(project: AwsCdkTypeScriptApp): CdkCli | undefined {
    const isDefined = (c: Component): c is CdkCli => c instanceof CdkCli;
    return project.components.find(isDefined);
  }

  /**
   * The consumer-supplied per-stage deploy default overrides.
   */
  public readonly deployDefaults: Partial<
    Record<AwsStageType, Partial<CdkDeployOptions>>
  >;

  /**
   * The consumer-supplied per-stage synth default overrides.
   */
  public readonly synthDefaults: Partial<
    Record<AwsStageType, Partial<CdkSynthOptions>>
  >;

  /**
   * The consumer-supplied per-stage watch default overrides.
   */
  public readonly watchDefaults: Partial<
    Record<AwsStageType, Partial<CdkWatchOptions>>
  >;

  /**
   * The consumer-supplied per-stage destroy default overrides.
   */
  public readonly destroyDefaults: Partial<
    Record<AwsStageType, Partial<CdkDestroyOptions>>
  >;

  /**
   * The consumer-supplied per-stage diff default overrides.
   */
  public readonly diffDefaults: Partial<
    Record<AwsStageType, Partial<CdkDiffOptions>>
  >;

  /**
   * The consumer-supplied per-stage bootstrap default overrides.
   */
  public readonly bootstrapDefaults: Partial<
    Record<AwsStageType, Partial<CdkBootstrapOptions>>
  >;

  /**
   * The consumer-supplied per-account overrides, keyed by AWS account ID.
   */
  public readonly accountOverrides: Record<string, CdkTargetOverrides>;

  /**
   * The resolved `aws-cdk` CLI version that generated CI jobs install.
   *
   * Always concrete, so callers rendering a workflow step never have to
   * re-apply the default. See {@link CdkCliOptions.cliVersion}.
   */
  public readonly cliVersion: string;

  constructor(project: AwsCdkTypeScriptApp, options: CdkCliOptions = {}) {
    super(project);
    this.deployDefaults = options.deployDefaults ?? {};
    this.synthDefaults = options.synthDefaults ?? {};
    this.watchDefaults = options.watchDefaults ?? {};
    this.destroyDefaults = options.destroyDefaults ?? {};
    this.diffDefaults = options.diffDefaults ?? {};
    this.bootstrapDefaults = options.bootstrapDefaults ?? {};
    this.accountOverrides = options.accountOverrides ?? {};

    /**
     * Default to the requirement the project already declares on `aws-cdk`.
     * Projen installs that same requirement as a devDependency, and its binary
     * is what the `synth` task runs — so defaulting here keeps the CLI that
     * deploys a cloud assembly identical to the one that built it.
     */
    this.cliVersion = options.cliVersion ?? project.cdkDeps.cdkCliVersion;
  }

  /**
   * Resolve the `cdk deploy` options to use against a given target.
   *
   * Folds the precedence chain via {@link mergeCdkOptions}: per-stage built-in
   * → per-stage consumer override → per-account override → per-target
   * override. The call-site explicit layer is applied by the caller after
   * this method returns. See {@link CdkCli} for the full chain.
   */
  public deployOptionsFor(target: AwsDeploymentTarget): CdkDeployOptions {
    const stage = target.awsStageType;
    return mergeCdkOptions<CdkDeployOptions>(
      CDK_DEPLOY_DEFAULTS_BY_STAGE[stage],
      this.deployDefaults[stage],
      this.accountOverrides[target.account]?.deploy,
      target.cdkOptions?.deploy,
    );
  }

  /**
   * Resolve the `cdk synth` options to use against a given target.
   *
   * Folds the precedence chain via {@link mergeCdkOptions}: per-stage built-in
   * → per-stage consumer override → per-account override → per-target
   * override. See {@link CdkCli} for the full chain.
   */
  public synthOptionsFor(target: AwsDeploymentTarget): CdkSynthOptions {
    const stage = target.awsStageType;
    return mergeCdkOptions<CdkSynthOptions>(
      CDK_SYNTH_DEFAULTS_BY_STAGE[stage],
      this.synthDefaults[stage],
      this.accountOverrides[target.account]?.synth,
      target.cdkOptions?.synth,
    );
  }

  /**
   * Resolve the `cdk watch` options to use against a given target.
   *
   * Folds the precedence chain via {@link mergeCdkOptions}: per-stage built-in
   * → per-stage consumer override → per-account override → per-target
   * override. See {@link CdkCli} for the full chain.
   */
  public watchOptionsFor(target: AwsDeploymentTarget): CdkWatchOptions {
    const stage = target.awsStageType;
    return mergeCdkOptions<CdkWatchOptions>(
      CDK_WATCH_DEFAULTS_BY_STAGE[stage],
      this.watchDefaults[stage],
      this.accountOverrides[target.account]?.watch,
      target.cdkOptions?.watch,
    );
  }

  /**
   * Resolve the `cdk destroy` options to use against a given target.
   *
   * Folds the precedence chain via {@link mergeCdkOptions}: per-stage built-in
   * → per-stage consumer override → per-account override → per-target
   * override. See {@link CdkCli} for the full chain.
   */
  public destroyOptionsFor(target: AwsDeploymentTarget): CdkDestroyOptions {
    const stage = target.awsStageType;
    return mergeCdkOptions<CdkDestroyOptions>(
      CDK_DESTROY_DEFAULTS_BY_STAGE[stage],
      this.destroyDefaults[stage],
      this.accountOverrides[target.account]?.destroy,
      target.cdkOptions?.destroy,
    );
  }

  /**
   * Resolve the `cdk diff` options to use against a given target.
   *
   * Folds the precedence chain via {@link mergeCdkOptions}: per-stage built-in
   * → per-stage consumer override → per-account override → per-target
   * override. See {@link CdkCli} for the full chain.
   */
  public diffOptionsFor(target: AwsDeploymentTarget): CdkDiffOptions {
    const stage = target.awsStageType;
    return mergeCdkOptions<CdkDiffOptions>(
      CDK_DIFF_DEFAULTS_BY_STAGE[stage],
      this.diffDefaults[stage],
      this.accountOverrides[target.account]?.diff,
      target.cdkOptions?.diff,
    );
  }

  /**
   * Resolve the `cdk bootstrap` options to use against a given target.
   *
   * Folds the precedence chain via {@link mergeCdkOptions}: per-stage built-in
   * → per-stage consumer override → per-account override → per-target
   * override. See {@link CdkCli} for the full chain.
   */
  public bootstrapOptionsFor(target: AwsDeploymentTarget): CdkBootstrapOptions {
    const stage = target.awsStageType;
    return mergeCdkOptions<CdkBootstrapOptions>(
      CDK_BOOTSTRAP_DEFAULTS_BY_STAGE[stage],
      this.bootstrapDefaults[stage],
      this.accountOverrides[target.account]?.bootstrap,
      target.cdkOptions?.bootstrap,
    );
  }
}
